import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, IconButton, Stack, TextField } from '@mui/material';
import { ContentCopy, EmailOutlined, IosShare, SmsOutlined } from '@mui/icons-material';
import { authService } from '../services/authService';
import { buildInviteLink } from '../utils/invite';
import { useNotification } from './NotificationContext';

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Invite a dive buddy": a personal link plus share shortcuts. Messages are drafted on the
 * inviter's own device (share sheet / SMS / mail client) — the app never sends email to
 * arbitrary addresses, so it can't be used to spam.
 */
const InviteDialog: React.FC<InviteDialogProps> = ({ open, onClose }) => {
  const { showSuccess, showError } = useNotification();
  const user = authService.getUser();
  if (!user) return null;

  const link = buildInviteLink(user.userId);
  const message = `Join me on New Achievement Scuba — where divers connect! ${link}`;
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showSuccess('Invite link copied');
    } catch {
      showError('Could not copy — select the link and copy it manually');
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: 'Join me on New Achievement Scuba', text: 'Join me — where divers connect!', url: link });
    } catch {
      /* dismissed the share sheet — nothing to do */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Invite a dive buddy</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Share your link. When a friend joins from it, they'll start out following you and you'll get a notification.
        </DialogContentText>
        <TextField
          fullWidth
          size="small"
          value={link}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="Copy invite link" onClick={copy} edge="end"><ContentCopy fontSize="small" /></IconButton>
              </InputAdornment>
            ),
          }}
          onFocus={(e) => e.target.select()}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          {canNativeShare && <Button variant="contained" startIcon={<IosShare />} onClick={nativeShare}>Share…</Button>}
          <Button variant="outlined" startIcon={<SmsOutlined />} component="a" href={`sms:?&body=${encodeURIComponent(message)}`}>Text</Button>
          <Button
            variant="outlined"
            startIcon={<EmailOutlined />}
            component="a"
            href={`mailto:?subject=${encodeURIComponent('Join me on New Achievement Scuba')}&body=${encodeURIComponent(message)}`}
          >
            Email
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteDialog;
